import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Card, Divider, Space, Tag } from "antd";
import SoftButton from "StyleFeatures/SoftButton";
import axiosInstance from "api/axiosInstance";
import API from "api/endpoints";
import Footer from "Common/Footer";

function Skills({ 
  onNext, 
  onBack, 
  initialData = {}, 
  isSubmitting,
  departmentId 
}) {
  const [skillsList, setSkillsList] = useState([]);
  const [selectedSkillIds, setSelectedSkillIds] = useState([]);
  const [ selectedDomainIds , setSelectedDomainIds ] = useState([]);
    const [customSkills, setCustomSkills] = useState([]);
  const [customSkillInput, setCustomSkillInput] = useState("");
  const [formErrors, setFormErrors] = useState({ skillError: "" });
  const [ domainList, setDomainList ] = useState([]);

  // Fetch skills and domains
  useEffect(() => {
    const fetchSkills = async () => {
      try {
        // Skills are a global catalog now (no department scoping), so we fetch
        // the full list regardless of the employee's department.
        const res = await axiosInstance.get(API.skills.getList);
        setSkillsList(res.data || []);
      } catch (error) {
        console.error("Error fetching skills list", error);
      }
    };

    const fetchDomains = async () => {
      try{
            const res = await axiosInstance.get(API.domains.getList);
            setDomainList(res.data || []);
      } catch(error){
        console.error("Error in fetching Domains", error);
      }
    };
    
    fetchSkills();
    fetchDomains();
  }, []);

  // Restore edit values
useEffect(() => {
  if (initialData) {
    setSelectedSkillIds(initialData.skillIds || []);
    setCustomSkills(initialData.customSkills || []);
    setSelectedDomainIds(initialData.domainIds || []);
  }
}, [initialData]);

  // Add dropdown skill as tag
  const handleSelectSkill = (e) => {
    const skillId = Number(e.target.value);
    if (!skillId) return;
    if (!selectedSkillIds.includes(skillId)) {
      setSelectedSkillIds([...selectedSkillIds, skillId]);
    }
    e.target.value = "";
  };

   // domain
  const handleSelectDomain = (e) => {
  const domainId = Number(e.target.value);
  if(!domainId) return;
  if(!selectedDomainIds.includes(domainId)){
    setSelectedDomainIds([...selectedDomainIds,domainId])
  }
  e.target.value="";
};

  // Add custom skill
  const addCustomSkill = () => {
    const skill = customSkillInput.trim();
    if (!skill) return;
    if (!customSkills.includes(skill)) {
      setCustomSkills([...customSkills, skill]);
    }
    setCustomSkillInput("");
  };

  // Remove selected skill
  const removeSkill = (id) => {
    setSelectedSkillIds(selectedSkillIds.filter((skill) => skill !== id));
  };

  const removeDomain = (id) => {
    setSelectedDomainIds(selectedDomainIds.filter((domain) => domain !== id));
  }

  // Remove custom skill
  const removeCustomSkill = (skill) => {
    setCustomSkills(customSkills.filter((s) => s !== skill));
  };

  // Submit
  const handleNext = () => {
   if (isSubmitting) return;
    setFormErrors({ skillError: "" });
    const payload = {
      domainIds : selectedDomainIds,
      skillIds: selectedSkillIds,
      customSkills,
    };
    onNext(payload);
  };

  const handlePreview = () => {
   if (isSubmitting) return;
    setFormErrors({ skillError: "" });
  };

  return (
    <div className="body flex-grow-1 px-3">
      <Card title="Skills" className="card-professional">
        <h5 className="fw-bold mb-4">Step 4: Select Domain and Skills </h5>
      {/* DOMAIN SECTION */}
        <div className="row mb-2">
          <div className="col-md-6">
            <label className="form-label fw-bold">Industry Domains</label>
            <select className="form-select" defaultValue="" onChange={handleSelectDomain}>
              <option value="">{domainList.length === 0 ? "No Domains available" : "Select Domain Knowledge"}</option>
              {domainList.map((dm) => (
                <option key={dm.id} value={dm.id}>{dm.name} ({dm.industry})</option>
              ))}
            </select>
          </div>
        </div>
        
        <div className="mb-4" style={{ marginTop: "10px" }}>
          <Space size={[8, 8]} wrap>
            {selectedDomainIds.map((id) => {
              const domain = domainList.find((d) => d.id === id);
              return (
                <Tag key={id} closable color="processing" onClose={() => removeDomain(id)} style={{ padding: "4px 10px", borderRadius: "4px" }}>
                  {domain?.name} <small className="opacity-50">| {domain?.industry}</small>
                </Tag>
              );
            })}
          </Space>
        </div>

        <Divider />
             
        <div className="row">
          {/* Skill Dropdown */}
          <div className="col-md-6 mb-4">
            <label className="form-label">Select Skill</label>
            <select
              className="form-select"
              defaultValue=""
              onChange={handleSelectSkill}
              disabled={skillsList.length === 0}
            >
              <option value="">
                {skillsList.length === 0 ? "No skills available" : "Select skill"}
              </option>

              {skillsList.map((skill) => (
                <option key={skill.id} value={skill.id}>
                  {skill.name}
                </option>
              ))}
            </select>

            {formErrors.skillError && (
              <div className="text-danger mt-2">{formErrors.skillError}</div>
            )}
          </div>
          {/* Custom Skill */}
          <div className="col-md-6 mb-4">
            <label className="form-label">Add Custom Skill</label>
            <div style={{ display: "flex", gap: "8px" }}>
              <input
                type="text"
                className="form-control"
                placeholder="Type skill"
                value={customSkillInput}
                onChange={(e) => setCustomSkillInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addCustomSkill();
                  }
                }}
              />
              <SoftButton className="btn-back" onClick={addCustomSkill} size="small">
                Add
              </SoftButton>
            </div>
          </div>
        </div>
        {/* Tags Display */}
        <div style={{ marginTop: "10px" }}>
          {selectedSkillIds.map((id) => {
            const skill = skillsList.find((s) => s.id === id);

            return (
              <Tag key={id} closable color="blue" onClose={() => removeSkill(id)}>
                {skill?.name}
              </Tag>
            );
          })}

          {customSkills.map((skill) => (
            <Tag key={skill} closable color="green" onClose={() => removeCustomSkill(skill)}>
              {skill}
            </Tag>
          ))}
        </div>

        {/* Buttons */}
        <div style={{ marginTop: "25px" }}>
          <SoftButton onClick={onBack} className="btn-back" style={{ marginRight: "10px" }}>
            Back
          </SoftButton>

           <SoftButton onClick={handlePreview} className="btn-back" style={{ marginRight: "10px" }}>
            Previe
          </SoftButton>

          <SoftButton
            onClick={handleNext}
            className="btn-primary"
            disabled={isSubmitting}// pass isSubmitting from parent
          >
            {isSubmitting ? "Submitting..." : "Submit"}
          </SoftButton>
        </div>
      </Card>

      <Footer />
    </div>
  );
}

Skills.propTypes = {
  onNext: PropTypes.func,
  onBack: PropTypes.func,
  initialData: PropTypes.object,
  isSubmitting: PropTypes.bool,
  departmentId: PropTypes.number,
};

export default Skills;
